'use client';

import { useEffect, useState } from 'react';
import {
  DATE_FORMAT_OPTIONS,
  generateRandomDates,
  type DateFormat,
} from '@/lib/browser-mockdata-extra-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

export default function BrowserRandomDateGenerator() {
  const [startDate, setStartDate] = useState('2020-01-01');
  const [endDate, setEndDate] = useState('2030-12-31');
  const [format, setFormat] = useState<DateFormat>('iso');
  const [count, setCount] = useState(10);
  const [output, setOutput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  function generate() {
    setCopyMessage(null);
    const result = generateRandomDates(startDate, endDate, format, count);
    setError(result.error);
    setOutput(result.output);
  }

  useEffect(() => {
    generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleCopy() {
    try {
      await copyText(output);
      setCopyMessage('Copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Random dates</span>
        <h2 className="tool-live-title">Random date generator</h2>
        <p className="tool-live-subtitle">
          Generate random dates in a chosen range and format, one per line, locally in your browser.
          Nothing is uploaded.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Start date</span>
              <input
                onChange={(event) => setStartDate(event.target.value)}
                type="date"
                value={startDate}
              />
            </label>
            <label className="tool-live-field">
              <span>End date</span>
              <input
                onChange={(event) => setEndDate(event.target.value)}
                type="date"
                value={endDate}
              />
            </label>
            <label className="tool-live-field">
              <span>Format</span>
              <select
                onChange={(event) => setFormat(event.target.value as DateFormat)}
                value={format}
              >
                {DATE_FORMAT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="tool-live-field">
              <span>How many dates</span>
              <input
                inputMode="numeric"
                onChange={(event) => setCount(Number(event.target.value))}
                type="number"
                value={count}
              />
            </label>
          </div>
          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={generate} type="button">
              Generate
            </button>
            {output ? (
              <button className="button button-secondary" onClick={handleCopy} type="button">
                Copy result
              </button>
            ) : null}
          </div>
          {error ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              {error}
            </p>
          ) : null}
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <label className="tool-live-field tool-live-field-full">
            <span>Generated dates</span>
            <textarea readOnly rows={16} value={output} />
          </label>
          <p className="tool-live-panel-note">
            Generated locally in your browser. Click generate for a fresh set (max 1000 dates).
          </p>
        </section>
      </div>
    </div>
  );
}
